import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../features/auth/store";
import { IS_API_MODE } from "../../shared/config/runtime";
import {
  login as loginRequest,
  loginWithEmailCode as loginWithEmailCodeRequest,
  register as registerRequest,
  sendEmailVerificationCode,
} from "../../features/auth/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { getPostLoginPath } from "../../features/auth/redirect";
import { showToast } from "../../shared/ui/toast";
import { initialPosts } from "../../shared/data/mockData";
import { Brand } from "../../widgets/AppShell/AppShell";
import PosterArtwork from "../../entities/post/ui/PosterArtwork";

const AUTH_MODE = {
  PASSWORD_LOGIN: "password-login",
  EMAIL_LOGIN: "email-login",
  REGISTER: "register",
};

const AUTH_MODE_META = {
  [AUTH_MODE.PASSWORD_LOGIN]: {
    title: "回到你的设计灵感库",
    description: "使用用户名和密码登录后，继续发现作品、设计师与合作机会。",
    submitText: "登录",
    submittingText: "正在登录……",
    successText: "登录成功",
    passwordAutoComplete: "current-password",
    passwordPlaceholder: "请输入密码",
    showUsername: true,
    showPassword: true,
    showNickname: false,
    showEmailVerification: false,
  },
  [AUTH_MODE.EMAIL_LOGIN]: {
    title: "验证码快速登录",
    description: "使用邮箱验证码快速登录，适合跨设备或临时访问。",
    submitText: "验证码登录",
    submittingText: "正在登录……",
    successText: "登录成功",
    showUsername: false,
    showPassword: false,
    showNickname: false,
    showEmailVerification: true,
    codePurpose: "login",
    codeButtonText: "发送登录码",
  },
  [AUTH_MODE.REGISTER]: {
    title: "让好设计被更多人看见",
    description: "加入图π，分享海报、宣传图与品牌视觉作品。",
    submitText: "创建账户",
    submittingText: "正在创建……",
    successText: "注册并登录成功",
    passwordAutoComplete: "new-password",
    passwordPlaceholder: "至少 8 个字符",
    showUsername: true,
    showPassword: true,
    showNickname: true,
    showEmailVerification: true,
    codePurpose: "register",
    codeButtonText: "发送验证码",
  },
};

function normalizeVerificationNotice(detail) {
  if (typeof detail !== "string") return "验证码已发送";
  if (detail.includes("如果条件满足")) return "验证码已发送";
  return detail.trim() || "验证码已发送";
}

function AuthPasswordInput({
  id,
  label,
  value,
  autoComplete,
  placeholder,
  visible,
  error,
  onChange,
  onToggle,
}) {
  const errorId = `${id}-error`;
  return (
    <label>
      <span>{label}</span>
      <div className="password-field">
        <input
          id={id}
          required
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={onToggle}
          aria-label={visible ? `隐藏${label}` : `显示${label}`}
        >
          {visible ? <EyeOff size={19} /> : <Eye size={19} />}
        </button>
      </div>
      {error ? <small id={errorId} className="auth-inline-error">{error}</small> : null}
    </label>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const startDemoSession = useAuthStore((state) => state.startDemoSession);
  const setSession = useAuthStore((state) => state.setSession);
  const [mode, setMode] = useState(AUTH_MODE.PASSWORD_LOGIN);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [visibleFields, setVisibleFields] = useState({});
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeCooldownByPurpose, setCodeCooldownByPurpose] = useState({
    login: 0,
    register: 0,
  });
  const [formError, setFormError] = useState("");
  const [formNotice, setFormNotice] = useState("");
  const modeMeta = AUTH_MODE_META[mode];
  const currentCodePurpose = modeMeta.codePurpose || "register";
  const currentCodeCooldown = codeCooldownByPurpose[currentCodePurpose] || 0;
  const passwordError = mode === AUTH_MODE.REGISTER && password && password.length < 8
    ? "新密码至少需要 8 个字符"
    : "";
  const confirmPasswordError = mode === AUTH_MODE.REGISTER && confirmPassword && password !== confirmPassword
    ? "两次输入的新密码不一致"
    : "";
  const registerPasswordInvalid = mode === AUTH_MODE.REGISTER
    && (password.length < 8 || password !== confirmPassword);

  useEffect(() => {
    if (!Object.values(codeCooldownByPurpose).some((seconds) => seconds > 0)) return undefined;
    const timerId = window.setTimeout(() => {
      setCodeCooldownByPurpose((current) => Object.fromEntries(
        Object.entries(current).map(([purpose, seconds]) => [purpose, Math.max(seconds - 1, 0)]),
      ));
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [codeCooldownByPurpose]);

  async function submitAuth(event) {
    event.preventDefault();
    setFormError("");
    setFormNotice("");
    if (mode === AUTH_MODE.REGISTER) {
      if (password.length < 8) {
        setFormError("新密码至少需要 8 个字符");
        return;
      }
      if (password !== confirmPassword) {
        setFormError("两次输入的新密码不一致");
        return;
      }
    }
    setSubmitting(true);
    try {
      if (IS_API_MODE) {
        if (mode === AUTH_MODE.REGISTER) {
          await registerRequest({
            username,
            password,
            nickname,
            email,
            verification_code: verificationCode,
          });
          const session = await loginRequest({ username, password });
          setSession(session);
        } else if (mode === AUTH_MODE.EMAIL_LOGIN) {
          const session = await loginWithEmailCodeRequest({
            email,
            verification_code: verificationCode,
          });
          setSession(session);
        } else {
          const session = await loginRequest({ username, password });
          setSession(session);
        }
      } else {
        startDemoSession();
      }
      showToast(modeMeta.successText);
      navigate(getPostLoginPath(location.state?.from), { replace: true });
    } catch (error) {
      setFormError(getApiErrorMessage(error, mode === AUTH_MODE.REGISTER ? "注册失败" : "登录失败"));
    } finally {
      setSubmitting(false);
    }
  }

  function toggleVisibility(field) {
    setVisibleFields((current) => ({ ...current, [field]: !current[field] }));
  }

  async function requestVerificationCode() {
    if (currentCodeCooldown > 0) return;
    if (!email.trim()) {
      setFormError("请先填写有效邮箱");
      return;
    }
    setSendingCode(true);
    setFormError("");
    try {
      if (IS_API_MODE) {
        const result = await sendEmailVerificationCode(email.trim(), modeMeta.codePurpose);
        setFormNotice(normalizeVerificationNotice(result.detail));
      } else {
        setFormNotice("演示模式：请输入任意 6 位验证码");
      }
      setCodeCooldownByPurpose((current) => ({
        ...current,
        [currentCodePurpose]: 60,
      }));
    } catch (error) {
      setFormError(getApiErrorMessage(error, "验证码发送失败"));
    } finally {
      setSendingCode(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-visual">
        <div className="auth-photo photo-a">
          <PosterArtwork artwork={initialPosts[0].artwork} />
        </div>
        <div className="auth-photo photo-b">
          <PosterArtwork artwork={initialPosts[1].artwork} />
        </div>
        <div className="auth-quote">
          <span>“</span>
          <p>每一个经过推敲的视觉创意，都值得被看见。</p>
        </div>
      </section>
      <section className="auth-panel">
        <Brand />
        <div className="auth-copy">
          <h1>{modeMeta.title}</h1>
          <p>{modeMeta.description}</p>
        </div>
        <div className="auth-mode-switch" role="tablist" aria-label="认证方式">
          <button
            type="button"
            className={mode === AUTH_MODE.PASSWORD_LOGIN ? "active" : ""}
            onClick={() => {
              setMode(AUTH_MODE.PASSWORD_LOGIN);
              setFormError("");
              setFormNotice("");
            }}
          >
            密码登录
          </button>
          <button
            type="button"
            className={mode === AUTH_MODE.EMAIL_LOGIN ? "active" : ""}
            onClick={() => {
              setMode(AUTH_MODE.EMAIL_LOGIN);
              setFormError("");
              setFormNotice("");
            }}
          >
            邮箱验证码登录
          </button>
          <button
            type="button"
            className={mode === AUTH_MODE.REGISTER ? "active" : ""}
            onClick={() => {
              setMode(AUTH_MODE.REGISTER);
              setFormError("");
              setFormNotice("");
            }}
          >
            注册
          </button>
        </div>
        <form onSubmit={submitAuth}>
          {modeMeta.showUsername ? (
            <label>
              <span>用户名</span>
              <input
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                placeholder={mode === AUTH_MODE.PASSWORD_LOGIN ? "请输入用户名" : "设置登录用户名"}
              />
            </label>
          ) : null}
          {modeMeta.showNickname ? (
            <label>
              <span>昵称</span>
              <input
                required
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                autoComplete="nickname"
                placeholder="大家怎么称呼你"
              />
            </label>
          ) : null}
          {modeMeta.showEmailVerification ? (
            <>
              <label>
                <span>邮箱</span>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  placeholder={mode === AUTH_MODE.EMAIL_LOGIN ? "请输入已注册邮箱" : "用于接收验证码"}
                />
              </label>
              <label>
                <span>验证码</span>
                <div className="verification-row">
                  <input
                    required
                    minLength={6}
                    maxLength={6}
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value)}
                    autoComplete="one-time-code"
                    placeholder="6 位验证码"
                  />
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={sendingCode || currentCodeCooldown > 0}
                    onClick={requestVerificationCode}
                  >
                    {sendingCode ? "发送中" : currentCodeCooldown > 0 ? `${currentCodeCooldown}s 后重试` : modeMeta.codeButtonText}
                  </button>
                </div>
              </label>
            </>
          ) : null}
          {modeMeta.showPassword ? (
            <AuthPasswordInput
              id="auth-password"
              label={mode === AUTH_MODE.REGISTER ? "密码" : "密码"}
              value={password}
              autoComplete={modeMeta.passwordAutoComplete}
              placeholder={modeMeta.passwordPlaceholder}
              visible={visibleFields.password}
              error={passwordError}
              onChange={(event) => setPassword(event.target.value)}
              onToggle={() => toggleVisibility("password")}
            />
          ) : null}
          {mode === AUTH_MODE.REGISTER ? (
            <AuthPasswordInput
              id="auth-confirm-password"
              label="确认密码"
              value={confirmPassword}
              autoComplete="new-password"
              placeholder="请再次输入密码"
              visible={visibleFields.confirmPassword}
              error={confirmPasswordError}
              onChange={(event) => setConfirmPassword(event.target.value)}
              onToggle={() => toggleVisibility("confirmPassword")}
            />
          ) : null}
          {formNotice && <div className="form-notice">{formNotice}</div>}
          {formError && <div className="form-error">{formError}</div>}
          <button className="primary-button auth-submit" disabled={submitting || registerPasswordInvalid}>
            {submitting ? modeMeta.submittingText : modeMeta.submitText}
          </button>
        </form>
        <p className="auth-switch">
          {mode === AUTH_MODE.REGISTER ? "已经有账户？" : "还没有账户？"}
          <button
            onClick={() => {
              setMode(mode === AUTH_MODE.REGISTER ? AUTH_MODE.PASSWORD_LOGIN : AUTH_MODE.REGISTER);
              setFormError("");
              setFormNotice("");
            }}
          >
            {mode === AUTH_MODE.REGISTER ? "返回登录" : "立即注册"}
          </button>
        </p>
        <small className="auth-legal">继续即表示你同意《用户协议》和《隐私政策》</small>
      </section>
    </main>
  );
}
